import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { resolveAssetUrl } from '../lib/assets';
import { ArrowLeft, Eye, Crown, Star, ShoppingBag, Sparkles, Code2, Check, ArrowRight, Palette } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const CER1 = 'https://images.unsplash.com/photo-1595351298020-038700609878?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const CER2 = 'https://images.unsplash.com/photo-1607556671927-78a6605e290b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800';
const CER3 = 'https://images.unsplash.com/photo-1609881583302-61548332039c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800';
const CER4 = 'https://images.unsplash.com/photo-1590605095243-072811dbe64c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800';

const PRODUCTS = [
  { name: 'Rokām darināta krūze', price: 18, img: CER4 },
  { name: 'Keramikas šķīvju komplekts', price: 45, img: CER3 },
  { name: 'Māla vāze «Rudens»', price: 32, img: CER2 },
  { name: 'Tējas servīze (2 pers.)', price: 56, img: CER1 },
  { name: 'Dekoratīvs puķu pods', price: 24, img: CER3 },
  { name: 'Sālstrauks & piparu trauks', price: 21, img: CER4 },
];

const PLANS = {
  basic: { name: 'Basic', price: 9, tier: 1, accent: '#4A6B53', dark: false,
    feats: ['Krāsas & fonti', 'Hero stils', 'Produktu izkārtojums', 'Custom CSS'] },
  premium: { name: 'Premium', price: 29, tier: 2, accent: '#D97736', dark: false,
    feats: ['Viss no Basic', 'Gatavie šabloni', 'Reklāmas baneri'] },
  premium_max: { name: 'Premium Max', price: 49, tier: 3, accent: '#7C3AED', dark: false,
    feats: ['Viss no Premium', 'Izceltie produkti', 'Dizains arī profilā', 'Melnraksts / Undo'] },
  premium_ultra: { name: 'Premium Ultra', price: 99, tier: 4, accent: '#EC4899', dark: true,
    feats: ['Viss no Max', 'Tēmu dzinējs', 'Headless API', 'Webhooks'] },
};

const PALETTE = ['#D97736', '#4A6B53', '#2EC4B6', '#7C3AED', '#EC4899', '#2563EB'];

export default function PremiumDemoPage() {
  const { plan } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [myListings, setMyListings] = useState([]);
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/my-listings`).then(({ data }) => setMyListings((data || []).slice(0, 6))).catch(() => {});
  }, [user]);
  const cfg = PLANS[plan] || PLANS.basic;
  const { tier, dark } = cfg;
  const [customAccent, setCustomAccent] = useState(null);
  const accent = customAccent || cfg.accent;
  const font = tier >= 2 ? "'Outfit', sans-serif" : "'Figtree', sans-serif";
  const bg = dark ? '#0A0A0A' : '#FAFAFA';
  const text = dark ? '#FFFFFF' : '#121212';
  const sub = dark ? 'rgba(255,255,255,0.6)' : '#5C5C5C';
  const card = dark ? 'rgba(255,255,255,0.05)' : '#FFFFFF';

  return (
    <div style={{ background: bg, minHeight: '100vh', fontFamily: font, color: text }} data-testid="premium-demo-page">
      {/* Demo banner */}
      <div className="sticky top-0 z-50 text-white px-4 py-2.5 flex items-center justify-between flex-wrap gap-2"
        style={{ background: accent }} data-testid="premium-demo-banner">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Eye className="w-4 h-4" /> DEMO — <Crown className="w-4 h-4" /> {cfg.name} plāns · €{cfg.price}/gadā
        </span>
        <div className="hidden md:flex items-center gap-1.5">
          {cfg.feats.map((f, i) => (
            <span key={i} className="text-[11px] bg-white/20 rounded-full px-2 py-0.5 flex items-center gap-1"><Check className="w-3 h-3" /> {f}</span>
          ))}
        </div>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 bg-black/25 hover:bg-black/40 rounded-full px-3 py-1 text-xs" data-testid="premium-demo-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Atpakaļ
        </button>
      </div>

      {/* Hero — scales with tier */}
      {tier >= 2 ? (
        <div className="relative h-[300px] overflow-hidden">
          <img src={CER1} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: dark
            ? `linear-gradient(0deg, #0A0A0A, rgba(10,10,10,0.35))`
            : `linear-gradient(0deg, rgba(0,0,0,0.55), rgba(0,0,0,0.15))` }} />
          <div className="relative h-full max-w-5xl mx-auto px-6 flex flex-col justify-end pb-8 text-white">
            {tier >= 4 && <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: accent }}><Sparkles className="w-3.5 h-3.5" /> Theme Engine</span>}
            <h1 className="text-4xl sm:text-5xl font-light tracking-tight">Keramikas Studija «Māla»</h1>
            <p className="text-white/80 mt-2 max-w-lg">Rokām darināta keramika no Latvijas māla — katrs darbs unikāls.</p>
          </div>
        </div>
      ) : (
        <div className="py-12 text-center text-white" style={{ background: accent }}>
          <h1 className="text-3xl sm:text-4xl font-bold">Keramikas Studija «Māla»</h1>
          <p className="opacity-90 mt-2">Rokām darināta keramika no Latvijas māla</p>
        </div>
      )}

      {/* Color customizer — try your brand colors on the demo live */}
      <div className="max-w-5xl mx-auto px-6 mt-6 flex items-center gap-2 flex-wrap" data-testid="color-customizer">
        <span className="text-sm font-semibold flex items-center gap-1.5" style={{ color: text }}><Palette className="w-4 h-4" style={{ color: accent }} /> Pielāgot krāsas:</span>
        {PALETTE.map((c) => (
          <button key={c} onClick={() => setCustomAccent(c)}
            className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
            style={{ background: c, borderColor: accent === c ? text : 'transparent' }}
            data-testid={`palette-${c.replace('#', '')}`} aria-label={c} />
        ))}
        {customAccent && (
          <button onClick={() => setCustomAccent(null)} className="text-xs underline ml-1" style={{ color: sub }} data-testid="palette-reset">
            Atiestatīt
          </button>
        )}
      </div>

      {/* Promo banner (premium+) */}
      {tier >= 2 && (
        <div className="max-w-5xl mx-auto px-6 mt-6">
          <div className="rounded-2xl px-5 py-3 text-sm font-semibold flex items-center gap-2"
            style={{ background: `${accent}1A`, color: accent }} data-testid="demo-promo-banner">
            🎁 Rudens akcija: -20% visiem šķīvju komplektiem · Bezmaksas piegāde no €40
          </div>
        </div>
      )}

      {/* Featured (max+) */}
      {tier >= 3 && (
        <div className="max-w-5xl mx-auto px-6 mt-10">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Star className="w-5 h-5" style={{ color: accent }} /> Izceltie produkti</h2>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {PRODUCTS.slice(0, 3).map((p, i) => (
              <div key={i} className="shrink-0 w-56 rounded-2xl overflow-hidden border" style={{ background: card, borderColor: dark ? 'rgba(255,255,255,0.1)' : '#E5E4E0' }}>
                <img src={p.img} alt="" className="w-full h-40 object-cover" />
                <div className="p-3"><p className="font-medium text-sm" style={{ color: text }}>{p.name}</p><p className="font-bold mt-1" style={{ color: accent }}>€{p.price}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Your real products in this plan's styling */}
      {myListings.length > 0 && (
        <div className="max-w-5xl mx-auto px-6 mt-10" data-testid="my-products-demo">
          <h2 className="text-xl font-semibold mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5" style={{ color: accent }} /> Kā tas izskatītos ar taviem produktiem</h2>
          <p className="text-sm mb-4" style={{ color: sub }}>Tavi reālie sludinājumi {cfg.name} plāna noformējumā</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {myListings.map((p, i) => (
              <div key={p.id} className="rounded-2xl overflow-hidden border transition-transform hover:-translate-y-1" style={{ background: card, borderColor: dark ? 'rgba(255,255,255,0.1)' : '#E5E4E0' }} data-testid={`my-product-${i}`}>
                {p.images?.[0]
                  ? <img src={resolveAssetUrl(p.images[0])} alt="" className="w-full h-36 object-cover" />
                  : <div className="w-full h-36 flex items-center justify-center" style={{ background: `${accent}14` }}><ShoppingBag className="w-8 h-8" style={{ color: accent }} /></div>}
                <div className="p-3">
                  <p className="font-medium text-sm truncate" style={{ color: text }}>{p.title}</p>
                  <div className="flex items-center justify-between mt-2">
                    {p.price != null ? <span className="font-bold" style={{ color: accent }}>€{p.price}</span> : <span />}
                    <button className="text-xs text-white rounded-full px-3 py-1.5" style={{ background: accent }}>Pirkt</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product grid */}
      <div className="max-w-5xl mx-auto px-6 mt-10 pb-16">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><ShoppingBag className="w-5 h-5" style={{ color: accent }} /> {myListings.length > 0 ? 'Piemēra produkti' : 'Visi produkti'}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {PRODUCTS.map((p, i) => (
            <div key={i} className="rounded-2xl overflow-hidden border transition-transform hover:-translate-y-1" style={{ background: card, borderColor: dark ? 'rgba(255,255,255,0.1)' : '#E5E4E0' }} data-testid={`demo-product-${i}`}>
              <img src={p.img} alt="" className="w-full h-36 object-cover" />
              <div className="p-3">
                <p className="font-medium text-sm" style={{ color: text }}>{p.name}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-bold" style={{ color: accent }}>€{p.price}</span>
                  <button className="text-xs text-white rounded-full px-3 py-1.5" style={{ background: accent }}>Pirkt</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Ultra — headless API showcase */}
        {tier >= 4 && (
          <div className="mt-12 rounded-2xl p-6 border" style={{ background: card, borderColor: 'rgba(255,255,255,0.1)' }} data-testid="demo-headless">
            <p className="font-semibold flex items-center gap-2 mb-2" style={{ color: text }}><Code2 className="w-5 h-5" style={{ color: accent }} /> Headless API</p>
            <p className="text-sm" style={{ color: sub }}>Savieno savu vietni ar KoLV veikalu, izmantojot REST API un webhooks — pilnīga kontrole pār dizainu.</p>
            <code className="block mt-3 text-xs rounded-lg px-3 py-2 font-mono" style={{ background: 'rgba(255,255,255,0.08)', color: accent }}>GET /api/v1/shop/products · X-API-Key: ••••••</code>
          </div>
        )}
      </div>

      {/* Conversion CTA */}
      <div className="sticky bottom-0 z-40 border-t px-4 py-3 flex items-center justify-center gap-3 flex-wrap"
        style={{ background: dark ? 'rgba(10,10,10,0.92)' : 'rgba(255,255,255,0.95)', borderColor: dark ? 'rgba(255,255,255,0.1)' : '#E5E4E0', backdropFilter: 'blur(8px)' }}>
        <span className="text-sm" style={{ color: sub }}>Patīk {cfg.name} plāns?</span>
        <button onClick={() => navigate(`/shop-profile?openPlans=1&plan=${plan}&theme=${accent.replace('#', '')}`)}
          className="inline-flex items-center gap-2 h-11 px-6 rounded-full text-white font-bold hover:opacity-90 transition-opacity"
          style={{ background: accent, boxShadow: `0 8px 30px ${accent}55` }}
          data-testid="activate-plan-cta">
          <Crown className="w-4 h-4" /> Aktivizēt {cfg.name} plānu <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
